import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, LayoutGrid, ListChecks, Store, Check, Loader2 } from 'lucide-react';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useToast } from '@/hooks/use-toast';
import { useMentorStatus } from '@/hooks/useMentorStatus';
import { useDeviceAppMode } from '@/hooks/useDeviceAppMode';
import { useDeviceMutations } from '@/hooks/useDeviceMutations';
import { useAppCategories } from '@/hooks/useAppCategories';
import InstalledAppsReviewModal from '@/components/InstalledAppsReviewModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const appManagementOptions = [
  {
    id: 'apps-by-category',
    title: 'Apps by Category',
    description: 'Apps are organized into categories like Social, Games, Productivity, and more. You control which categories are allowed. New apps in approved categories are automatically available without individual approval.',
    store: 'Shomri App Store',
    details: [
      'Control access by category (Social, Games, etc.)',
      'New apps in allowed categories are automatically available',
      'Whitelist or blacklist specific apps',
      'Best balance of control and flexibility',
    ],
    icon: LayoutGrid,
    gradient: 'from-primary to-accent',
  },
  {
    id: 'approved-only',
    title: 'Approved Apps Only',
    description: 'Maximum control over every app. Only apps you specifically approve can be installed. Each new app requires your explicit permission. Best for younger users or when you want complete oversight.',
    store: 'Shomri App Store',
    details: [
      'Only explicitly approved apps can be installed',
      'Every new app requires individual approval',
      'Complete control over app access',
      'Best for younger users needing strict oversight',
    ],
    icon: ListChecks,
    gradient: 'from-primary to-primary-hover',
  },
  {
    id: 'open-blocklist',
    title: 'Open App Store + Block List',
    description: 'Full access to the Apple App Store with the ability to block specific apps. Users can download any app except those you explicitly block. Best for mature users who need minimal restrictions.',
    store: 'Apple App Store',
    details: [
      'Full access to the Apple App Store',
      'Block specific apps as needed',
      'Minimal restrictions with override capability',
      'Best for mature users who need freedom',
    ],
    icon: Store,
    gradient: 'from-primary/80 to-accent/80',
  },
];

const AppModeSelect: React.FC = () => {
  const navigate = useNavigate();
  const { serial } = useEffectiveSerial();
  const { toast } = useToast();
  const { canChangeDirectly } = useMentorStatus();
  const { currentMode, setAppMode } = useDeviceAppMode();
  const { updateAppSettings } = useDeviceMutations();
  const { deviceAppConfig, categories, allowedApps } = useAppCategories();
  
  const [selectedOption, setSelectedOption] = useState<typeof appManagementOptions[0] | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const currentOption = appManagementOptions.find((opt) => opt.id === currentMode) || appManagementOptions[0];

  // Redirect mentees/children back - they can't change modes
  React.useEffect(() => {
    if (!canChangeDirectly) {
      navigate(`/${serial}/settings/app-management`, { replace: true });
    }
  }, [canChangeDirectly, navigate, serial]);

  const handleOptionSelect = (option: typeof appManagementOptions[0]) => {
    if (option.id === currentMode) return;
    setSelectedOption(option);
    setShowConfirmation(true);
  };

  const handleConfirmRequest = async () => {
    if (!selectedOption) return;

    if (canChangeDirectly) {
      setIsSaving(true);
      try {
        // Special case: whitelist → categories — prune apps already covered by allowed categories
        if (currentMode === 'approved-only' && selectedOption.id === 'apps-by-category') {
          // allowedApps is destructured from useAppCategories() above

          // Build set of allowed category names
          const allowedCategoryNames = new Set(
            categories.filter(c => c.isAllowed).map(c => c.name)
          );

          // Get raw device-level allowed IDs
          const getDeviceIds = (config: Record<string, any> | undefined) =>
            Object.entries(config || {})
              .filter(([_, value]) => value === 'device')
              .map(([id]) => id);

          const deviceAllowedIds = getDeviceIds(deviceAppConfig?.allowedApps as any);
          const deviceBlockedIds = getDeviceIds(deviceAppConfig?.blockedApps as any);

          // Keep only apps whose category is NOT already allowed by categories
          // (i.e., they need explicit whitelisting as overrides)
          const filteredAllowedIds = deviceAllowedIds.filter(id => {
            const appInfo = allowedApps.find(a => a.id === id);
            if (!appInfo?.category) return true; // keep if no category info
            return !allowedCategoryNames.has(appInfo.category);
          });

          const removedCount = deviceAllowedIds.length - filteredAllowedIds.length;

          // Build current appFilter
          const currentAppFilter: Record<string, boolean> = {};
          categories.forEach((cat) => {
            currentAppFilter[cat.name] = cat.isAllowed;
          });

          await updateAppSettings({
            allowedApps: filteredAllowedIds as any,
            blockedApps: deviceBlockedIds as any,
            appFilter: currentAppFilter,
          });
          await setAppMode(selectedOption.id);

          toast({
            title: "Settings Updated",
            description: removedCount > 0
              ? `Switched to "${selectedOption.title}". Removed ${removedCount} app${removedCount !== 1 ? 's' : ''} already covered by allowed categories.`
              : `Switched to "${selectedOption.title}".`,
          });
        } else {
          await setAppMode(selectedOption.id);
          toast({
            title: "Settings Updated",
            description: `Switched to "${selectedOption.title}".`,
          });
        }
        navigate(`/${serial}/settings/app-management`);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to update setting. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      toast({
        title: "Request Submitted",
        description: `Your request to switch to "${selectedOption.title}" has been submitted.`,
      });
      navigate(`/${serial}/settings/app-management`);
    }
    setShowConfirmation(false);
    setSelectedOption(null);
  };

  const handleReviewDone = async (allowedAppIds: string[]) => {
    setShowReviewModal(false);
    setIsSaving(true);
    try {
      // Full-state replacement: must send allowedApps, blockedApps, and appFilter together
      const currentBlockedApps = deviceAppConfig?.blockedApps || {};
      const deviceBlockedIds = Object.entries(currentBlockedApps)
        .filter(([_, value]) => value === 'device')
        .map(([id]) => id);

      // Build current appFilter state
      const currentAppFilter: Record<string, boolean> = {};
      categories.forEach((cat) => {
        currentAppFilter[cat.name] = cat.isAllowed;
      });

      await updateAppSettings({
        allowedApps: allowedAppIds as any,
        blockedApps: deviceBlockedIds as any,
        appFilter: currentAppFilter,
      });
      await setAppMode('approved-only');
      toast({
        title: "Settings Updated",
        description: `Switched to "Approved Apps Only".`,
      });
      navigate(`/${serial}/settings/app-management`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update setting. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
      setSelectedOption(null);
    }
  };

  const handleReviewCancel = () => {
    setShowReviewModal(false);
    setSelectedOption(null);
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-background to-muted/30 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(`/${serial}/settings/app-management`)}
            className="p-2 -ml-2 rounded-full hover:bg-muted/50 active:scale-95 transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-foreground" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-foreground">Select App Mode</h1>
            <p className="text-[13px] text-muted-foreground">Choose how apps are managed</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-6 space-y-4">
        <p className="text-[13px] text-muted-foreground px-1">
          Select an app management mode to control how apps are installed and accessed on this device. Each mode offers different levels of control and flexibility.
        </p>

        {appManagementOptions.map((option) => {
          const Icon = option.icon;
          const isCurrentMode = option.id === currentMode;
          
          return (
            <button
              key={option.id}
              onClick={() => !isCurrentMode && handleOptionSelect(option)}
              disabled={isCurrentMode}
              className={`w-full p-5 rounded-2xl border text-left transition-all ${
                isCurrentMode 
                  ? 'border-primary bg-primary/5 cursor-default' 
                  : 'border-border/50 bg-background hover:bg-muted/30 active:scale-[0.99] shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${option.gradient} flex items-center justify-center shadow-md flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-[16px] text-foreground">{option.title}</p>
                    {isCurrentMode && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                        <Check className="w-3 h-3" />
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-muted-foreground leading-relaxed mb-2">
                    {option.description}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-3">
                    {option.store}
                  </p>
                  <ul className="space-y-1.5">
                    {option.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[12px] text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 mt-1.5 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Mode Change Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent className="bg-background">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {canChangeDirectly 
                ? `Switch to ${selectedOption?.title}?`
                : 'Request App Mode Change'
              }
            </AlertDialogTitle>
            <AlertDialogDescription>
              {canChangeDirectly 
                ? `This will change the app management mode to "${selectedOption?.title}".`
                : <>You are requesting to change from "{currentOption.title}" to "{selectedOption?.title}". This request will be sent for approval.</>
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedOption(null)} disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRequest} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : canChangeDirectly ? 'Confirm' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Installed Apps Review Modal for Whitelist */}
      <InstalledAppsReviewModal
        open={showReviewModal}
        onDone={handleReviewDone}
        onCancel={handleReviewCancel}
      />
    </div>
  );
};

export default AppModeSelect;
